import { HTMLProps, forwardRef } from 'react';
import { css, Theme } from '@emotion/react';
import { darken } from 'polished';
import { User } from '@phosphor-icons/react';
import { Typography } from 'client/components/Typography';
import { borderRadius } from 'client/Theme';

export interface PostProps extends HTMLProps<HTMLDivElement> {
  author?: { name: string };
  watermark?: JSX.Element;
  outline?: boolean;
}

export const Post = forwardRef<HTMLDivElement, PostProps>(
  ({ author = null, watermark = null, outline = false, children, ...props }, ref) => {
    return (
      <article ref={ref} css={[postStyle, outline && outlineStyle]} {...props}>
        <div>
          {children}
          {!!author && (
            <div css={authorStyle}>
              <User />
              <Typography>{author.name}</Typography>
            </div>
          )}
        </div>
        {!!watermark && <watermark.type css={watermarkStyle} {...watermark.props} />}
      </article>
    );
  }
);

const postStyle = (theme: Theme) => css`
  --padding: 48px;

  position: relative;
  padding: var(--padding);
  background-color: ${theme.color.grey[60]};
  border-radius: ${borderRadius}px;
  overflow: hidden;

  > div {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;
    z-index: 1;
  }

  p {
    color: ${theme.color.grey[100]};
  }
`;

const outlineStyle = (theme: Theme) => css`
  padding: calc(var(--padding) - 2px);
  border: 2px solid ${theme.color.grey[60]};
  background-color: transparent;
`;

const authorStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;

  > svg {
    padding: 6px;
    width: 32px;
    height: 32px;
    background-color: ${theme.color.background};
    border-radius: 50%;
    color: ${theme.color.grey[80]};
  }
`;

const watermarkStyle = (theme: Theme) => css`
  position: absolute;
  right: -80px;
  bottom: -140px;
  width: 400px;
  height: 400px;
  color: ${darken(0.03, theme.color.grey[60])};
  z-index: 0;
`;
